import { useState, useCallback, useRef } from 'react'
import { Upload, X, FileText, AlertCircle } from 'lucide-react'
import { productsApi } from '@api/client'
import { showToast } from '@store/uiStore'
import { clsx } from 'clsx'

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

interface AdminProduct {
  id: number
  name: string
  vendor: string
  currency: string
  base_price: number
  pro_price: number
  enterprise_price: number
  cost_price: number
}

type UploadMode = 'replace' | 'update' | 'append'

interface ParsedProduct {
  name: string
  vendor: string
  currency: string
  base_price: number
  pro_price: number
  enterprise_price: number
  cost_price: number
  _status?: 'new' | 'update' | 'same'
}

interface Props {
  existingProducts: AdminProduct[]
  onClose: () => void
  onUploadComplete: () => void
}

// ─────────────────────────────────────────────
// COLUMN ALIASES for flexible CSV mapping
// ─────────────────────────────────────────────

const COLUMN_ALIASES: Record<string, string> = {
  // name
  'name': 'name', 'product': 'name', 'product_name': 'name', 'productname': 'name',
  // vendor
  'vendor': 'vendor', 'supplier': 'vendor', 'provider': 'vendor',
  // currency
  'currency': 'currency', 'ccy': 'currency', 'curr': 'currency',
  // base_price
  'base_price': 'base_price', 'baseprice': 'base_price', 'base': 'base_price', 'standard': 'base_price', 'basic': 'base_price', 'basic_price': 'base_price',
  // pro_price
  'pro_price': 'pro_price', 'proprice': 'pro_price', 'pro': 'pro_price', 'professional': 'pro_price', 'growth': 'pro_price', 'growth_price': 'pro_price',
  // enterprise_price
  'enterprise_price': 'enterprise_price', 'enterpriseprice': 'enterprise_price', 'enterprise': 'enterprise_price', 'ent': 'enterprise_price', 'ent_price': 'enterprise_price',
  // cost_price
  'cost_price': 'cost_price', 'costprice': 'cost_price', 'cost': 'cost_price', 'buyprice': 'cost_price', 'buy_price': 'cost_price', 'cogs': 'cost_price',
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function normaliseName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '').trim()
}

function parseNumericValue(val: string | number | undefined): number {
  if (val === undefined || val === null || val === '') return 0
  if (typeof val === 'number') return val
  // Strip currency prefixes like "AUD $2.85" or "$2.85"
  const cleaned = String(val).replace(/[^0-9.\-]/g, '')
  const num = parseFloat(cleaned)
  return isNaN(num) ? 0 : num
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.split('\n').filter(l => l.trim())
  if (lines.length < 2) return []

  // Parse header
  const headers = parseCSVLine(lines[0])

  return lines.slice(1).map(line => {
    const values = parseCSVLine(line)
    const row: Record<string, string> = {}
    headers.forEach((h, i) => {
      row[h.trim()] = (values[i] || '').trim()
    })
    return row
  })
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

function mapRow(row: Record<string, string>): ParsedProduct {
  const mapped: Record<string, string> = {}

  for (const [key, value] of Object.entries(row)) {
    const normalised = key.toLowerCase().replace(/[^a-z_]/g, '')
    const targetField = COLUMN_ALIASES[normalised]
    if (targetField) {
      mapped[targetField] = value
    }
  }

  return {
    name: mapped.name || '',
    vendor: mapped.vendor || '',
    currency: (mapped.currency || 'AUD').toUpperCase(),
    base_price: parseNumericValue(mapped.base_price),
    pro_price: parseNumericValue(mapped.pro_price),
    enterprise_price: parseNumericValue(mapped.enterprise_price),
    cost_price: parseNumericValue(mapped.cost_price),
  }
}

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

export function FileUploadModal({ existingProducts, onClose, onUploadComplete }: Props) {
  const [mode, setMode] = useState<UploadMode>('update')
  const [parsedProducts, setParsedProducts] = useState<ParsedProduct[]>([])
  const [fileName, setFileName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Analysis counts
  const newCount = parsedProducts.filter(p => p._status === 'new').length
  const updateCount = parsedProducts.filter(p => p._status === 'update').length
  const sameCount = parsedProducts.filter(p => p._status === 'same').length

  // ─── Parse & analyse uploaded file ───
  const processFile = useCallback((file: File) => {
    setFileName(file.name)
    const reader = new FileReader()

    reader.onload = (e) => {
      const text = e.target?.result as string
      let products: ParsedProduct[] = []

      if (file.name.endsWith('.json')) {
        try {
          const json = JSON.parse(text)
          const arr = Array.isArray(json) ? json : json.products || []
          products = arr.map((item: Record<string, unknown>) => ({
            name: String(item.name || item.productName || ''),
            vendor: String(item.vendor || item.supplier || ''),
            currency: String(item.currency || 'AUD').toUpperCase(),
            base_price: parseNumericValue(item.base_price as string ?? item.basePrice as string),
            pro_price: parseNumericValue(item.pro_price as string ?? item.proPrice as string),
            enterprise_price: parseNumericValue(item.enterprise_price as string ?? item.enterprisePrice as string),
            cost_price: parseNumericValue(item.cost_price as string ?? item.costPrice as string),
          }))
        } catch {
          showToast('Invalid JSON file', 'error')
          return
        }
      } else {
        // CSV
        const rows = parseCSV(text)
        products = rows.map(mapRow).filter(p => p.name)
      }

      if (products.length === 0) {
        showToast('No valid products found in file', 'error')
        return
      }

      // Analyse: tag each product as new, update, or same
      const existingNameMap = new Map<string, AdminProduct>()
      for (const ep of existingProducts) {
        existingNameMap.set(normaliseName(ep.name), ep)
      }

      const EPSILON = 0.0001
      for (const p of products) {
        const existing = existingNameMap.get(normaliseName(p.name))
        if (!existing) {
          p._status = 'new'
        } else {
          const priceChanged =
            Math.abs(existing.base_price - p.base_price) > EPSILON ||
            Math.abs(existing.pro_price - p.pro_price) > EPSILON ||
            Math.abs(existing.enterprise_price - p.enterprise_price) > EPSILON ||
            Math.abs(existing.cost_price - p.cost_price) > EPSILON
          p._status = priceChanged ? 'update' : 'same'
        }
      }

      setParsedProducts(products)
    }

    reader.readAsText(file)
  }, [existingProducts])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }, [processFile])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }, [processFile])

  // ─── Upload ───
  const handleUpload = async () => {
    if (parsedProducts.length === 0) return
    setUploading(true)

    // Strip internal _status field before sending
    const cleaned = parsedProducts.map(({ _status, ...rest }) => rest)
    const res = await productsApi.bulkUpload(mode, cleaned)

    if (res.success && res.data) {
      const d = res.data
      const parts = []
      if (d.inserted > 0) parts.push(`${d.inserted} added`)
      if (d.updated > 0) parts.push(`${d.updated} updated`)
      if (d.skipped > 0) parts.push(`${d.skipped} skipped`)
      showToast(`Upload complete: ${parts.join(', ')}`, 'success')
      onUploadComplete()
    } else {
      showToast(res.error || 'Upload failed', 'error')
    }
    setUploading(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Upload Products</h2>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Import products from CSV or JSON file</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors">
            <X size={16} className="text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Upload Mode */}
          <div>
            <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-2 block">Upload Mode</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'replace' as UploadMode, label: 'Replace All', desc: 'Delete all existing, import fresh' },
                { value: 'update' as UploadMode, label: 'Update & Add', desc: 'Update matching names, add new' },
                { value: 'append' as UploadMode, label: 'Add New Only', desc: 'Skip existing, add only new' },
              ]).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setMode(opt.value)}
                  className={clsx(
                    'p-3 rounded-lg border text-left transition-colors',
                    mode === opt.value
                      ? 'border-primary bg-primary/5'
                      : 'border-[var(--border-color)] hover:border-[var(--text-muted)]'
                  )}
                >
                  <div className="text-[11px] font-medium text-[var(--text-primary)]">{opt.label}</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
            {mode === 'replace' && (
              <div className="flex items-center gap-2 mt-2 text-[10px] text-warning">
                <AlertCircle size={12} />
                This will delete ALL existing products and replace them with the uploaded file.
              </div>
            )}
          </div>

          {/* Drop Zone */}
          {parsedProducts.length === 0 ? (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={clsx(
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                dragOver ? 'border-primary bg-primary/5' : 'border-[var(--border-color)] hover:border-[var(--text-muted)]'
              )}
            >
              <Upload size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
              <p className="text-[11px] text-[var(--text-primary)] font-medium">
                Drop your file here or click to browse
              </p>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                Supports CSV and JSON files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <>
              {/* File Info & Analysis */}
              <div className="bg-[var(--bg-tertiary)] rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-primary" />
                    <span className="text-[11px] font-medium text-[var(--text-primary)]">{fileName}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">({parsedProducts.length} products)</span>
                  </div>
                  <button
                    onClick={() => { setParsedProducts([]); setFileName('') }}
                    className="text-[10px] text-[var(--text-muted)] hover:text-danger transition-colors"
                  >
                    Remove
                  </button>
                </div>

                {/* Analysis Summary */}
                <div className="flex items-center gap-4 text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-success" />
                    <span className="text-[var(--text-secondary)]">{newCount} new</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-warning" />
                    <span className="text-[var(--text-secondary)]">{updateCount} updates</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gray-400" />
                    <span className="text-[var(--text-secondary)]">{sameCount} unchanged</span>
                  </div>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[300px]">
                  <table className="w-full">
                    <thead className="sticky top-0">
                      <tr className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                        <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Status</th>
                        <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Name</th>
                        <th className="text-left text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Vendor</th>
                        <th className="text-center text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">CCY</th>
                        <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Base</th>
                        <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Pro</th>
                        <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Ent</th>
                        <th className="text-right text-[10px] text-[var(--text-muted)] font-medium px-3 py-2">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-color)]">
                      {parsedProducts.slice(0, 20).map((p, i) => (
                        <tr key={i} className="hover:bg-[var(--bg-secondary)]/50">
                          <td className="px-3 py-2">
                            <span className={clsx(
                              'text-[9px] px-1.5 py-0.5 rounded font-bold uppercase',
                              p._status === 'new' && 'bg-success/20 text-success',
                              p._status === 'update' && 'bg-warning/20 text-warning',
                              p._status === 'same' && 'bg-gray-500/20 text-gray-400'
                            )}>
                              {p._status === 'new' ? 'NEW' : p._status === 'update' ? 'UPD' : 'SAME'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-primary)] font-medium max-w-[180px] truncate">{p.name}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)]">{p.vendor || '—'}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)] text-center">{p.currency}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-primary)] text-right font-mono">{p.base_price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-primary)] text-right font-mono">{p.pro_price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-primary)] text-right font-mono">{p.enterprise_price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)] text-right font-mono">{p.cost_price.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedProducts.length > 20 && (
                  <div className="px-3 py-2 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
                    <span className="text-[10px] text-[var(--text-muted)]">
                      Showing first 20 of {parsedProducts.length} products
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-color)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={parsedProducts.length === 0 || uploading}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 text-[11px] font-medium rounded-lg transition-colors',
              parsedProducts.length > 0
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed'
            )}
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={13} />
                Upload {parsedProducts.length > 0 ? `${parsedProducts.length} Products` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
